import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Briefcase, BadgePercent, Newspaper, Settings, LogOut, ShieldCheck, Menu, X, Mail, TrendingUp, Sparkles, Scale, Undo2, Search
} from "lucide-react";

import { adminMe } from "@/lib/api";

const MENU = [
  { href: "/admin", label: "Dashboard", Icon: LayoutDashboard, exact: true },
  { href: "/admin/checks", label: "Analyses", Icon: FileText },
  { href: "/admin/revenue", label: "Omzet", Icon: TrendingUp },
  { href: "/admin/refunds", label: "Refunds", Icon: Undo2 },
  { href: "/admin/businesses", label: "Bedrijven", Icon: Briefcase },
  { href: "/admin/discount-codes", label: "Kortingscodes", Icon: BadgePercent },
  { href: "/admin/blog", label: "Blog", Icon: Newspaper },
  { href: "/admin/auto-blog", label: "Auto-blog", Icon: Sparkles },
  { href: "/admin/legal-rules", label: "Legal Rules", Icon: Scale },
  { href: "/admin/legal-pages", label: "Juridische pagina's", Icon: FileText },
  { href: "/admin/email-templates", label: "E-mails", Icon: Mail },
  { href: "/admin/config", label: "Configuratie", Icon: Settings },
  { href: "/admin/seo", label: "SEO", Icon: Search },
  { href: "/admin/settings", label: "Account & 2FA", Icon: ShieldCheck },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(undefined);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (location.pathname === "/admin/login") return;
    adminMe()
      .then(setMe)
      .catch(() => setMe(null));
  }, [location.pathname]);

  function logout() {
    localStorage.removeItem("ce_admin_token");
    navigate("/admin/login");
  }

  if (location.pathname === "/admin/login") return <>{children}</>;
  if (me === undefined) return <div className="min-h-screen flex items-center justify-center text-ink-soft">Laden…</div>;
  if (me === null) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-secondary flex" data-testid="admin-layout">
      <aside
        className={`fixed lg:sticky top-0 left-0 z-30 h-screen w-64 bg-slate-900 text-white flex-shrink-0 transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="px-6 py-5 flex items-center gap-2 border-b border-white/10">
          <span className="inline-flex h-9 w-9 rounded-xl bg-blue-500 items-center justify-center text-white">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <div className="font-display text-base font-bold tracking-tight">
              Check<span className="text-blue-300">Expert</span>
            </div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Admin</div>
          </div>
          <button className="ml-auto lg:hidden text-slate-300" onClick={() => setOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="py-4 space-y-1 px-3 text-sm">
          {MENU.map(({ href, label, Icon, exact }) => {
            const active = exact ? location.pathname === href : location.pathname.startsWith(href);
            return (
              <Link
                key={href}
                to={href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  active ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
                onClick={() => setOpen(false)}
                data-testid={`admin-nav-${label.toLowerCase()}`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 text-xs">
          <div className="text-slate-400 truncate">{me.email}</div>
          <button
            onClick={logout}
            className="mt-2 inline-flex items-center gap-2 text-slate-300 hover:text-white text-xs"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4" /> Uitloggen
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-border">
          <button onClick={() => setOpen(true)} className="p-2"><Menu className="h-5 w-5" /></button>
          <div className="font-display font-bold">CheckExpert Admin</div>
          <div />
        </header>
        <main className="p-6 lg:p-10">{children}</main>
      </div>

      {open && <div className="fixed inset-0 z-20 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}
